<template>
  <div v-if="loaded" class="form-step-container">
    <!-- תגית השלב -->
    <div class="step-badge">{{ words.step }} {{ step }}</div>
    <h1 class="main-title">שלום {{ form.first_name }}, {{ words.whats_your_address }}</h1>

    <form class="form_steps" @submit.prevent="submit">
      <!-- כרטיסייה 1: בחירת קהילה -->
      <div class="card-box">
        <label class="card-title">{{ words.community_input_label }}</label>

        <template v-if="communityInfo && communityInfo.name">
          <div class="input_like">
            <span>{{ communityInfo.name }}</span>
            <button type="button" class="change_btn" @click="clearCommunity">
              {{ words.change_number }}
            </button>
          </div>
        </template>

        <div v-else class="form-item select_wrapper">
          <Select2
            v-model="selectedCommunityId"
            :required="true"
            :settings="settingsCommunity"
            @select="onSelectCommunity"
            @click="focusInput"
          />
        </div>
      </div>

      <!-- כרטיסייה 2: פרטי כתובת -->
      <div class="card-box">
        <label class="card-title">{{ words.whats_your_address }}</label>

        <div class="options_grid mb-10">
          <div class="form-item floating_label">
            <input 
              id="city" 
              v-model.trim="form.city" 
              type="text" 
              autocomplete="address-level2" 
              placeholder=" "
              required 
            />
            <label for="city">{{ words.city }}</label>
          </div>

          <div class="form-item floating_label">
            <input 
              id="street" 
              v-model.trim="form.street" 
              type="text" 
              autocomplete="address-line1" 
              placeholder=" "
              required 
            />
            <label for="street">{{ words.street }}</label>
          </div>
        </div>

        <div class="options_grid single_col">
          <div class="form-item floating_label">
            <input 
              id="house_num" 
              v-model.trim="form.house_num" 
              type="text" 
              inputmode="numeric" 
              autocomplete="off" 
              placeholder=" "
              required 
              @keypress="isNumber" 
            />
            <label for="house_num">{{ words.house_num }}</label>
          </div>
        </div>
      </div>

      <!-- כפתורי פעולה בתחתית -->
      <div class="action-footer">
        <button 
          type="button" 
          class="btn-back" 
          @click="emit('returnStep')"
        >
          ➔ <span>{{ words.return_step }}</span>
        </button>

        <input type="submit" class="btn btn-primary" :value="words.procced" />
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import Select2 from 'vue3-select2-component'

// Props
const props = defineProps({
  numbersInfo: { type: Array, required: true },
  step: { type: Number, required: true },
  numbers: { type: Array, required: true },
  parentForm: { type: Object, required: true }
})

// Emits
const emit = defineEmits(['saveData', 'returnStep'])

const store = useStore()

// State
const form = reactive({ ...props.parentForm })
const communityInfo = ref({})
const selectedCommunityId = ref('')
const loaded = ref(false)

// Computed
const words = computed(() => store.state.words || {})

// Select2 Settings
const settingsCommunity = reactive({
  language: {},
  dir: 'rtl',
  ajax: {
    url: (window.api_url || '') + 'get_communities',
    headers: { Authorization: localStorage.getItem('user_token') || '' },
    data(params) {
      return { search: params.term, _type: 'query', name: params.term }
    },
    processResults(response) {
      const list = response?.data?.items || []
      return { results: list.map(item => ({ id: item.id, text: item.name })) }
    }
  }
})

// Lifecycle
onMounted(() => {
  settingsCommunity.language = {
    noResults: () => words.value.no_results,
    searching: () => words.value.searching
  }

  if (typeof api === 'function') {
    api({ action: 'cart/get_community_info', method: 'post' }, data => {
      if (data?.data?.community) {
        communityInfo.value = data.data.community
        selectedCommunityId.value = data.data.community.id
      }
      loaded.value = true
    })
  } else {
    loaded.value = true
  }
})

// Methods
const isNumber = (evt) => {
  const charCode = evt.which ? evt.which : evt.keyCode
  if (charCode > 31 && (charCode < 48 || charCode > 57)) {
    evt.preventDefault()
  }
}

const clearCommunity = () => {
  communityInfo.value = {}
  selectedCommunityId.value = ''
}

const onSelectCommunity = () => {
  if (typeof api === 'function') {
    api({
      action: 'cart/save_community',
      data: { community: selectedCommunityId.value }
    })
  }
}

const focusInput = () => {
  setTimeout(() => {
    document.querySelector('.select2-search__field')?.focus()
  }, 100)
}

const submit = () => {
  emit('saveData', {
    ...form,
    community_id: selectedCommunityId.value
  })
}
</script>

<style scoped lang="scss">

$purple-primary: #6B11D8;
$purple-light: #F5EFFF;
$purple-hover: #550bb1;
$bg-light: #F8F9FD;
$text-dark: #2B2635;
$text-muted: #718096;
$border-color: #E2E8F0;
$radius: 16px;

.form-step-container {
  direction: rtl;
  text-align: right;
  font-family: inherit;

  /* מניעת גלילה והתאמה לגובה המסך */
  max-height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;

 .step-badge {
  display: block;
  width: 100%;               /* מותח את הרקע לרוחב כל השורה */
  box-sizing: border-box;
  background: #F5EFFF;       /* רקע סגול בהיר */
  color: #6B11D8;            /* צבע טקסט סגול */
  font-size: 13px;
  font-weight: 700;
  padding: 8px 16px;
  border-radius: 20px;
  margin-bottom: 12px;
  text-align: right;         /* יישור הטקסט לימין */
}

  .main-title {
    font-size: 22px;
    font-weight: 800;
    color: $text-dark;
    margin: 0 0 12px 0;
  }

  .form_steps {
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
    justify-content: space-between;
  }

  /* כרטיסיות עיצוב */
  .card-box {
    background: #ffffff;
    border: 1px solid $border-color;
    border-radius: $radius;
    padding: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);

    .card-title {
      display: block;
      font-size: 14px;
      font-weight: 700;
      color: $text-dark;
      margin-bottom: 12px;
    }
  }

  .input_like {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background-color: $purple-light;
    border: 1.5px solid $purple-primary;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    color: $text-dark;

    .change_btn {
      background: none;
      border: none;
      color: $purple-primary;
      font-size: 13px;
      font-weight: 700;
      text-decoration: underline;
      cursor: pointer;
      transition: color 0.2s ease;

      &:hover {
        color: $purple-hover;
      }
    }
  }

  .options_grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 10px;

    &.single_col {
      grid-template-columns: 1fr;
      max-width: 180px;
    }
  }

  .mb-10 {
    margin-bottom: 10px;
  }

  /* תווית צפה (Floating Label) */
  .floating_label {
    position: relative;
    width: 100%;

    input {
      width: 100%;
      padding: 10px 14px;
      border: 1.5px solid $border-color;
      border-radius: 12px;
      font-size: 14px;
      outline: none;
      transition: all 0.2s ease;
      background: #ffffff;
      color: $text-dark;
      box-sizing: border-box;

      &:focus {
        border-color: $purple-primary;
        box-shadow: 0 0 0 3px rgba(107, 17, 216, 0.1);
      }

      &:focus + label,
      &:not(:placeholder-shown) + label {
        top: 0;
        right: 10px;
        transform: translateY(-50%);
        font-size: 11px;
        font-weight: 600;
        color: $purple-primary;
        background-color: #ffffff;
        padding: 0 6px;
      }
    }

    label {
      position: absolute;
      top: 50%;
      right: 14px;
      transform: translateY(-50%);
      font-size: 13px;
      color: $text-muted;
      pointer-events: none;
      transition: all 0.2s ease;
    }
  }

  /* סרגל כפתורים בתחתית */
  .action-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-top: auto;
    padding-top: 10px;

    .btn-back {
      background: transparent;
      border: none;
      color: $text-muted;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      padding: 8px 12px;
      border-radius: 20px;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      transition: all 0.2s ease;

      &:hover {
        color: $purple-primary;
        background-color: $purple-light;
      }
    }

    .btn-primary {
      border: none;
      background: $purple-primary;
      color: #ffffff;
      padding: 10px 24px;
      border-radius: 30px;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: 0 4px 12px rgba(107, 17, 216, 0.2);

      &:hover {
        background: $purple-hover;
        box-shadow: 0 6px 16px rgba(107, 17, 216, 0.3);
      }
    }
  }
}

/* התאמת עיצוב לרכיב Select2 החיצוני */
:deep(.select2-container--default) {
  width: 100% !important;

  .select2-selection--single {
    height: 44px;
    border: 1.5px solid $border-color;
    border-radius: 12px;
    display: flex;
    align-items: center;
    padding: 0 8px;
    transition: all 0.2s ease;

    .select2-selection__rendered {
      color: $text-dark;
      font-size: 14px;
    }

    .select2-selection__arrow {
      height: 100%;
      top: 0;
    }
  }

  &.select2-container--open .select2-selection--single {
    border-color: $purple-primary;
    box-shadow: 0 0 0 3px rgba(107, 17, 216, 0.1);
  }
}
</style>